/**
 * Enumerações do domínio. Os valores são idênticos aos enums do Prisma
 * (prisma/schema.prisma) — mantenha-os sincronizados.
 */

const values = <T extends Record<string, string>>(o: T) => Object.values(o) as T[keyof T][];

export const InstitutionRole = {
  ADMIN_INSTITUTION: 'ADMIN_INSTITUTION',
  COORDINATOR: 'COORDINATOR',
  ADVISOR: 'ADVISOR',
  STUDENT: 'STUDENT',
} as const;
export type InstitutionRole = (typeof InstitutionRole)[keyof typeof InstitutionRole];
export const INSTITUTION_ROLES = values(InstitutionRole);

export const WorkRole = {
  LEAD_ADVISOR: 'LEAD_ADVISOR',
  CO_ADVISOR: 'CO_ADVISOR',
  COLLABORATOR: 'COLLABORATOR',
  STUDENT: 'STUDENT',
  REVIEWER: 'REVIEWER',
} as const;
export type WorkRole = (typeof WorkRole)[keyof typeof WorkRole];
export const WORK_ROLES = values(WorkRole);

/** Papel efetivo usado pela matriz de permissões. */
export const Role = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN_INSTITUTION: 'ADMIN_INSTITUTION',
  COORDINATOR: 'COORDINATOR',
  ADVISOR: 'ADVISOR',
  LEAD_ADVISOR: 'LEAD_ADVISOR',
  CO_ADVISOR: 'CO_ADVISOR',
  COLLABORATOR: 'COLLABORATOR',
  STUDENT: 'STUDENT',
  REVIEWER: 'REVIEWER',
} as const;
export type Role = (typeof Role)[keyof typeof Role];

export const MembershipStatus = { ACTIVE: 'ACTIVE', SUSPENDED: 'SUSPENDED', ARCHIVED: 'ARCHIVED' } as const;
export type MembershipStatus = (typeof MembershipStatus)[keyof typeof MembershipStatus];

export const ParticipantStatus = { ACTIVE: 'ACTIVE', REMOVED: 'REMOVED' } as const;
export type ParticipantStatus = (typeof ParticipantStatus)[keyof typeof ParticipantStatus];

export const UserStatus = { ACTIVE: 'ACTIVE', DISABLED: 'DISABLED' } as const;
export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];

export const WorkStatus = {
  DRAFT: 'DRAFT',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  ACTIVE: 'ACTIVE',
  SUSPENDED: 'SUSPENDED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const;
export type WorkStatus = (typeof WorkStatus)[keyof typeof WorkStatus];
export const WORK_STATUSES = values(WorkStatus);

export const AutonomyLevel = {
  /** (a) tudo passa pela orientação */
  FULL_REVIEW: 'FULL_REVIEW',
  /** (b) tarefas de baixo risco são publicadas automaticamente */
  AUTO_LOW_RISK: 'AUTO_LOW_RISK',
} as const;
export type AutonomyLevel = (typeof AutonomyLevel)[keyof typeof AutonomyLevel];

export const PolicyScope = {
  GLOBAL: 'GLOBAL',
  INSTITUTION: 'INSTITUTION',
  PROGRAM: 'PROGRAM',
  MODALITY: 'MODALITY',
  WORK: 'WORK',
} as const;
export type PolicyScope = (typeof PolicyScope)[keyof typeof PolicyScope];

export const PolicyVersionStatus = { DRAFT: 'DRAFT', PUBLISHED: 'PUBLISHED', SUPERSEDED: 'SUPERSEDED' } as const;
export type PolicyVersionStatus = (typeof PolicyVersionStatus)[keyof typeof PolicyVersionStatus];

export const CheckpointKind = { CHECKPOINT: 'CHECKPOINT', MILESTONE: 'MILESTONE' } as const;
export type CheckpointKind = (typeof CheckpointKind)[keyof typeof CheckpointKind];

export const CheckpointStatus = {
  PLANNED: 'PLANNED',
  IN_PROGRESS: 'IN_PROGRESS',
  SUBMITTED: 'SUBMITTED',
  CHANGES_REQUESTED: 'CHANGES_REQUESTED',
  APPROVED: 'APPROVED',
  WAIVED: 'WAIVED',
} as const;
export type CheckpointStatus = (typeof CheckpointStatus)[keyof typeof CheckpointStatus];
export const CHECKPOINT_DONE_STATUSES: CheckpointStatus[] = ['APPROVED', 'WAIVED'];
/** Em aberto: o(a) orientando(a) ainda preenche (nem enviada, nem concluída). */
export const CHECKPOINT_OPEN_STATUSES: CheckpointStatus[] = ['PLANNED', 'IN_PROGRESS', 'CHANGES_REQUESTED'];

export const ExtensionStatus = {
  REQUESTED: 'REQUESTED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
} as const;
export type ExtensionStatus = (typeof ExtensionStatus)[keyof typeof ExtensionStatus];

export const TaskPublication = { DRAFT: 'DRAFT', PUBLISHED: 'PUBLISHED', ARCHIVED: 'ARCHIVED' } as const;
export type TaskPublication = (typeof TaskPublication)[keyof typeof TaskPublication];

/** Estado declarado pelo aluno. */
export const TaskStudentStatus = { NOT_DONE: 'NOT_DONE', IN_PROGRESS: 'IN_PROGRESS', DONE: 'DONE' } as const;
export type TaskStudentStatus = (typeof TaskStudentStatus)[keyof typeof TaskStudentStatus];

/** Estado de validação docente (independente do declarado). */
export const TaskValidationStatus = {
  PENDING: 'PENDING',
  CONFIRMED: 'CONFIRMED',
  CHANGES_REQUESTED: 'CHANGES_REQUESTED',
} as const;
export type TaskValidationStatus = (typeof TaskValidationStatus)[keyof typeof TaskValidationStatus];

export const TaskPriority = { LOW: 'LOW', MEDIUM: 'MEDIUM', HIGH: 'HIGH' } as const;
export type TaskPriority = (typeof TaskPriority)[keyof typeof TaskPriority];

export const TaskOrigin = {
  ADVISOR: 'ADVISOR',
  MEETING: 'MEETING',
  AI: 'AI',
  CHECKIN: 'CHECKIN',
  STUDENT: 'STUDENT',
} as const;
export type TaskOrigin = (typeof TaskOrigin)[keyof typeof TaskOrigin];

export const RiskLevel = { LOW: 'LOW', NORMAL: 'NORMAL' } as const;
export type RiskLevel = (typeof RiskLevel)[keyof typeof RiskLevel];

export const DeliverableStatus = {
  OPEN: 'OPEN',
  SUBMITTED: 'SUBMITTED',
  CHANGES_REQUESTED: 'CHANGES_REQUESTED',
  APPROVED: 'APPROVED',
  ARCHIVED: 'ARCHIVED',
} as const;
export type DeliverableStatus = (typeof DeliverableStatus)[keyof typeof DeliverableStatus];

export const VersionStatus = {
  SUBMITTED: 'SUBMITTED',
  CHANGES_REQUESTED: 'CHANGES_REQUESTED',
  APPROVED: 'APPROVED',
  SUPERSEDED: 'SUPERSEDED',
} as const;
export type VersionStatus = (typeof VersionStatus)[keyof typeof VersionStatus];

export const ScanStatus = { NOT_SCANNED: 'NOT_SCANNED', PENDING: 'PENDING', CLEAN: 'CLEAN', INFECTED: 'INFECTED' } as const;
export type ScanStatus = (typeof ScanStatus)[keyof typeof ScanStatus];

export const FeedbackKind = { COMMENT: 'COMMENT', CHANGE_REQUEST: 'CHANGE_REQUEST', APPROVAL: 'APPROVAL' } as const;
export type FeedbackKind = (typeof FeedbackKind)[keyof typeof FeedbackKind];

export const FeedbackTarget = {
  WORK: 'WORK',
  CHECKPOINT: 'CHECKPOINT',
  TASK: 'TASK',
  DELIVERABLE_VERSION: 'DELIVERABLE_VERSION',
  CHECKIN: 'CHECKIN',
} as const;
export type FeedbackTarget = (typeof FeedbackTarget)[keyof typeof FeedbackTarget];

export const Visibility = { ALL_PARTICIPANTS: 'ALL_PARTICIPANTS', ADVISORS_ONLY: 'ADVISORS_ONLY' } as const;
export type Visibility = (typeof Visibility)[keyof typeof Visibility];

export const CheckInStatus = {
  OPEN: 'OPEN',
  SUBMITTED: 'SUBMITTED',
  REVIEWED: 'REVIEWED',
  MISSED: 'MISSED',
} as const;
export type CheckInStatus = (typeof CheckInStatus)[keyof typeof CheckInStatus];

export const MeetingStatus = { SCHEDULED: 'SCHEDULED', HELD: 'HELD', CANCELLED: 'CANCELLED' } as const;
export type MeetingStatus = (typeof MeetingStatus)[keyof typeof MeetingStatus];

export const MinutesStatus = { NONE: 'NONE', DRAFT: 'DRAFT', PUBLISHED: 'PUBLISHED' } as const;
export type MinutesStatus = (typeof MinutesStatus)[keyof typeof MinutesStatus];

export const RequestType = {
  RENEGOTIATE_DEADLINE: 'RENEGOTIATE_DEADLINE',
  CONTEST_TASK: 'CONTEST_TASK',
  CONTEST_FEEDBACK: 'CONTEST_FEEDBACK',
  OTHER: 'OTHER',
} as const;
export type RequestType = (typeof RequestType)[keyof typeof RequestType];

export const RequestTargetType = { CHECKPOINT: 'CHECKPOINT', TASK: 'TASK', FEEDBACK: 'FEEDBACK', WORK: 'WORK' } as const;
export type RequestTargetType = (typeof RequestTargetType)[keyof typeof RequestTargetType];

export const RequestStatus = { OPEN: 'OPEN', ACCEPTED: 'ACCEPTED', REJECTED: 'REJECTED', WITHDRAWN: 'WITHDRAWN' } as const;
export type RequestStatus = (typeof RequestStatus)[keyof typeof RequestStatus];

/** Nível do(a) orientando(a). */
export const AcademicLevel = {
  INICIACAO_CIENTIFICA: 'INICIACAO_CIENTIFICA',
  GRADUACAO: 'GRADUACAO',
  ESPECIALIZACAO: 'ESPECIALIZACAO',
  MESTRADO: 'MESTRADO',
  DOUTORADO: 'DOUTORADO',
  POS_DOUTORADO: 'POS_DOUTORADO',
} as const;
export type AcademicLevel = (typeof AcademicLevel)[keyof typeof AcademicLevel];
export const ACADEMIC_LEVELS = Object.values(AcademicLevel) as AcademicLevel[];

/** Tipos de trabalho sugeridos por nível (o(a) orientador(a) pode ajustar no convite). */
export const LEVEL_DEFAULT_MODALITIES: Record<AcademicLevel, string[]> = {
  INICIACAO_CIENTIFICA: ['PESQUISA', 'ARTIGO_CONFERENCIA', 'ARTIGO'],
  GRADUACAO: ['TCC', 'PESQUISA', 'ARTIGO', 'ARTIGO_CONFERENCIA'],
  ESPECIALIZACAO: ['MONOGRAFIA', 'ARTIGO', 'ARTIGO_CONFERENCIA'],
  MESTRADO: ['DISSERTACAO', 'ARTIGO', 'ARTIGO_CONFERENCIA', 'PESQUISA'],
  DOUTORADO: ['TESE', 'ARTIGO', 'ARTIGO_CONFERENCIA', 'PESQUISA'],
  POS_DOUTORADO: ['POS_DOUTORADO', 'ARTIGO', 'ARTIGO_CONFERENCIA', 'PESQUISA'],
};

export const NotificationType = {
  WORK_ADDED: 'WORK_ADDED',
  WORK_PROPOSED: 'WORK_PROPOSED',
  WORK_APPROVAL_DECIDED: 'WORK_APPROVAL_DECIDED',
  ADVISING_LINKED: 'ADVISING_LINKED',
  ADVISING_JOIN_REQUESTED: 'ADVISING_JOIN_REQUESTED',
  ADVISING_JOIN_DECIDED: 'ADVISING_JOIN_DECIDED',
  STEP_CREATED: 'STEP_CREATED',
  STEP_SUBMITTED: 'STEP_SUBMITTED',
  STEP_REVIEWED: 'STEP_REVIEWED',
  STEP_SUGGESTED: 'STEP_SUGGESTED',
  TASK_PUBLISHED: 'TASK_PUBLISHED',
  TASK_OVERDUE: 'TASK_OVERDUE',
  TASK_DECLARED: 'TASK_DECLARED',
  TASK_VALIDATED: 'TASK_VALIDATED',
  CHECKIN_OPEN: 'CHECKIN_OPEN',
  CHECKIN_REMINDER: 'CHECKIN_REMINDER',
  CHECKIN_MISSED_ALERT: 'CHECKIN_MISSED_ALERT',
  CHECKIN_SUBMITTED: 'CHECKIN_SUBMITTED',
  DELIVERABLE_SUBMITTED: 'DELIVERABLE_SUBMITTED',
  DELIVERABLE_REVIEWED: 'DELIVERABLE_REVIEWED',
  FEEDBACK_CREATED: 'FEEDBACK_CREATED',
  DEADLINE_CHANGED: 'DEADLINE_CHANGED',
  CHECKPOINT_UPDATED: 'CHECKPOINT_UPDATED',
  MILESTONE_APPROACHING: 'MILESTONE_APPROACHING',
  REQUEST_CREATED: 'REQUEST_CREATED',
  REQUEST_DECIDED: 'REQUEST_DECIDED',
  MEETING_SCHEDULED: 'MEETING_SCHEDULED',
  MINUTES_PUBLISHED: 'MINUTES_PUBLISHED',
  WEEKLY_DIGEST: 'WEEKLY_DIGEST',
  REPORT_READY: 'REPORT_READY',
  AI_ANALYSIS_READY: 'AI_ANALYSIS_READY',
} as const;
export type NotificationType = (typeof NotificationType)[keyof typeof NotificationType];
export const NOTIFICATION_TYPES = values(NotificationType);

export const AiAnalysisStatus = {
  QUEUED: 'QUEUED',
  RUNNING: 'RUNNING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
} as const;
export type AiAnalysisStatus = (typeof AiAnalysisStatus)[keyof typeof AiAnalysisStatus];

export const AiTargetType = { DELIVERABLE_VERSION: 'DELIVERABLE_VERSION', CHECKIN: 'CHECKIN', MEETING: 'MEETING' } as const;
export type AiTargetType = (typeof AiTargetType)[keyof typeof AiTargetType];

export const AiSuggestionKind = {
  FINDING: 'FINDING',
  PROMISE_CHECK: 'PROMISE_CHECK',
  TASK_DRAFT: 'TASK_DRAFT',
  QUESTION: 'QUESTION',
  MINUTES_DRAFT: 'MINUTES_DRAFT',
} as const;
export type AiSuggestionKind = (typeof AiSuggestionKind)[keyof typeof AiSuggestionKind];

export const VerificationStatus = {
  EVIDENCED: 'EVIDENCED',
  HYPOTHESIS: 'HYPOTHESIS',
  UNVERIFIED: 'UNVERIFIED',
} as const;
export type VerificationStatus = (typeof VerificationStatus)[keyof typeof VerificationStatus];

export const SuggestionReviewStatus = {
  PENDING: 'PENDING',
  ACCEPTED: 'ACCEPTED',
  EDITED: 'EDITED',
  REJECTED: 'REJECTED',
} as const;
export type SuggestionReviewStatus = (typeof SuggestionReviewStatus)[keyof typeof SuggestionReviewStatus];

export const ReportType = { WORK_SHEET_PDF: 'WORK_SHEET_PDF', WORKS_CSV: 'WORKS_CSV', TASKS_CSV: 'TASKS_CSV' } as const;
export type ReportType = (typeof ReportType)[keyof typeof ReportType];

export const ReportStatus = { QUEUED: 'QUEUED', RUNNING: 'RUNNING', COMPLETED: 'COMPLETED', FAILED: 'FAILED' } as const;
export type ReportStatus = (typeof ReportStatus)[keyof typeof ReportStatus];

export const TrafficLight = { GREEN: 'GREEN', YELLOW: 'YELLOW', RED: 'RED' } as const;
export type TrafficLight = (typeof TrafficLight)[keyof typeof TrafficLight];

/** Rótulos em pt-BR para a interface. */
export const LABELS = {
  role: {
    SUPER_ADMIN: 'Superadministrador',
    ADMIN_INSTITUTION: 'Administração institucional',
    COORDINATOR: 'Coordenação',
    ADVISOR: 'Docente orientador(a)',
    LEAD_ADVISOR: 'Orientador(a) principal',
    CO_ADVISOR: 'Coorientador(a)',
    COLLABORATOR: 'Colaborador(a)',
    STUDENT: 'Estudante',
    REVIEWER: 'Revisor(a)',
  } satisfies Record<Role, string>,
  workStatus: {
    DRAFT: 'Rascunho',
    PENDING_APPROVAL: 'Aguardando confirmação',
    ACTIVE: 'Em andamento',
    SUSPENDED: 'Suspenso',
    COMPLETED: 'Concluído',
    CANCELLED: 'Cancelado',
  } satisfies Record<WorkStatus, string>,
  checkpointStatus: {
    PLANNED: 'Planejado',
    IN_PROGRESS: 'Em andamento',
    SUBMITTED: 'Enviado',
    CHANGES_REQUESTED: 'Ajustes solicitados',
    APPROVED: 'Aprovado',
    WAIVED: 'Dispensado',
  } satisfies Record<CheckpointStatus, string>,
  taskStudentStatus: {
    NOT_DONE: 'Não feita',
    IN_PROGRESS: 'Em andamento',
    DONE: 'Feita',
  } satisfies Record<TaskStudentStatus, string>,
  taskValidationStatus: {
    PENDING: 'Aguardando validação',
    CONFIRMED: 'Confirmada',
    CHANGES_REQUESTED: 'Ajustes solicitados',
  } satisfies Record<TaskValidationStatus, string>,
  taskPublication: { DRAFT: 'Rascunho', PUBLISHED: 'Publicada', ARCHIVED: 'Arquivada' } satisfies Record<
    TaskPublication,
    string
  >,
  taskPriority: { LOW: 'Baixa', MEDIUM: 'Média', HIGH: 'Alta' } satisfies Record<TaskPriority, string>,
  taskOrigin: {
    ADVISOR: 'Orientação',
    MEETING: 'Reunião',
    AI: 'Sugestão de IA (revisada)',
    CHECKIN: 'Check-in',
    STUDENT: 'Estudante',
  } satisfies Record<TaskOrigin, string>,
  deliverableStatus: {
    OPEN: 'Aguardando envio',
    SUBMITTED: 'Em revisão',
    CHANGES_REQUESTED: 'Ajustes solicitados',
    APPROVED: 'Aprovada',
    ARCHIVED: 'Arquivada',
  } satisfies Record<DeliverableStatus, string>,
  versionStatus: {
    SUBMITTED: 'Enviada',
    CHANGES_REQUESTED: 'Ajustes solicitados',
    APPROVED: 'Aprovada',
    SUPERSEDED: 'Substituída',
  } satisfies Record<VersionStatus, string>,
  checkInStatus: {
    OPEN: 'Aberto',
    SUBMITTED: 'Enviado',
    REVIEWED: 'Revisado',
    MISSED: 'Sem resposta',
  } satisfies Record<CheckInStatus, string>,
  extensionStatus: {
    REQUESTED: 'Solicitada',
    APPROVED: 'Aprovada',
    REJECTED: 'Rejeitada',
    CANCELLED: 'Cancelada',
  } satisfies Record<ExtensionStatus, string>,
  requestType: {
    RENEGOTIATE_DEADLINE: 'Renegociação de prazo',
    CONTEST_TASK: 'Contestação de tarefa',
    CONTEST_FEEDBACK: 'Contestação de feedback',
    OTHER: 'Outra solicitação',
  } satisfies Record<RequestType, string>,
  requestStatus: {
    OPEN: 'Aberta',
    ACCEPTED: 'Aceita',
    REJECTED: 'Recusada',
    WITHDRAWN: 'Retirada',
  } satisfies Record<RequestStatus, string>,
  meetingStatus: { SCHEDULED: 'Agendada', HELD: 'Realizada', CANCELLED: 'Cancelada' } satisfies Record<
    MeetingStatus,
    string
  >,
  academicLevel: {
    INICIACAO_CIENTIFICA: 'Iniciação científica',
    GRADUACAO: 'Graduação',
    ESPECIALIZACAO: 'Especialização',
    MESTRADO: 'Mestrado',
    DOUTORADO: 'Doutorado',
    POS_DOUTORADO: 'Pós-doutorado',
  } satisfies Record<AcademicLevel, string>,
  /** Nível com o artigo, para frases como "Ingresso na iniciação científica". */
  academicLevelIn: {
    INICIACAO_CIENTIFICA: 'na iniciação científica',
    GRADUACAO: 'na graduação',
    ESPECIALIZACAO: 'na especialização',
    MESTRADO: 'no mestrado',
    DOUTORADO: 'no doutorado',
    POS_DOUTORADO: 'no pós-doutorado',
  } satisfies Record<AcademicLevel, string>,
  /** Situação de uma etapa de entrega, do ponto de vista de quem acompanha. */
  stepStatus: {
    PLANNED: 'A fazer',
    IN_PROGRESS: 'Em preenchimento',
    SUBMITTED: 'Aguardando avaliação',
    CHANGES_REQUESTED: 'Ajustes solicitados',
    APPROVED: 'Concluída',
    WAIVED: 'Dispensada',
  } satisfies Record<CheckpointStatus, string>,
  trafficLight: { GREEN: 'Em dia', YELLOW: 'Atenção', RED: 'Crítico' } satisfies Record<TrafficLight, string>,
  scanStatus: {
    NOT_SCANNED: 'Não verificado por antivírus',
    PENDING: 'Verificação pendente',
    CLEAN: 'Limpo',
    INFECTED: 'Bloqueado (ameaça detectada)',
  } satisfies Record<ScanStatus, string>,
  notificationType: {
    WORK_ADDED: 'Inclusão em trabalho',
    WORK_PROPOSED: 'Trabalho cadastrado por orientando(a)',
    WORK_APPROVAL_DECIDED: 'Confirmação de trabalho',
    ADVISING_LINKED: 'Novo vínculo de orientação',
    ADVISING_JOIN_REQUESTED: 'Pedidos de ingresso pelo link de orientação',
    ADVISING_JOIN_DECIDED: 'Resposta ao pedido de ingresso',
    STEP_CREATED: 'Novas atividades',
    STEP_SUBMITTED: 'Atividades enviadas para avaliação',
    STEP_REVIEWED: 'Avaliação de atividades',
    STEP_SUGGESTED: 'Atividades sugeridas por orientando(a)',
    TASK_PUBLISHED: 'Novas tarefas',
    TASK_OVERDUE: 'Tarefas vencidas',
    TASK_DECLARED: 'Tarefas marcadas como feitas',
    TASK_VALIDATED: 'Validação de tarefas',
    CHECKIN_OPEN: 'Abertura de check-in',
    CHECKIN_REMINDER: 'Lembretes de prazo e check-in',
    CHECKIN_MISSED_ALERT: 'Check-in sem resposta',
    CHECKIN_SUBMITTED: 'Check-ins enviados',
    DELIVERABLE_SUBMITTED: 'Entregas enviadas',
    DELIVERABLE_REVIEWED: 'Revisão de entregas',
    FEEDBACK_CREATED: 'Comentários',
    DEADLINE_CHANGED: 'Mudanças de prazo',
    CHECKPOINT_UPDATED: 'Atualização de checkpoints',
    MILESTONE_APPROACHING: 'Marcos institucionais próximos',
    REQUEST_CREATED: 'Solicitações recebidas',
    REQUEST_DECIDED: 'Respostas a solicitações',
    MEETING_SCHEDULED: 'Reuniões agendadas',
    MINUTES_PUBLISHED: 'Atas publicadas',
    WEEKLY_DIGEST: 'Resumo semanal',
    REPORT_READY: 'Relatórios prontos',
    AI_ANALYSIS_READY: 'Análises de IA prontas',
  } satisfies Record<NotificationType, string>,
  verification: {
    EVIDENCED: 'Com evidência',
    HYPOTHESIS: 'Hipótese',
    UNVERIFIED: 'Não verificado',
  } satisfies Record<VerificationStatus, string>,
} as const;
